import { ReactNode } from "react";
import { Input, Select, Switch } from "@headlessui/react";
import clsx from "clsx";
import { INPUT_CLASS, SELECT_CLASS, SWITCH_CLASS } from "../tableStyles";

/** The label + control row both forms repeated about six times each. */
export function FieldRow({
  label,
  children,
  hint,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
}) {
  return (
    <div className="sm:flex items-center">
      <div className="w-4/12 text-sm/6 font-medium opacity-40">{label}</div>
      <div className="sm:w-8/12 text-sm/6 opacity-70">
        {children}
        {hint && <div className="text-xs opacity-50 mt-1">{hint}</div>}
      </div>
    </div>
  );
}

export function TextField({
  label,
  value,
  onChange,
  hint,
  list,
  placeholder,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
  list?: string;
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <FieldRow label={label} hint={hint}>
      <Input
        value={value}
        list={list}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={clsx(INPUT_CLASS, disabled && "opacity-50")}
      />
    </FieldRow>
  );
}

export function NumberField({
  label,
  value,
  onChange,
  hint,
  placeholder,
}: {
  label: string;
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  hint?: string;
  placeholder?: string;
}) {
  return (
    <FieldRow label={label} hint={hint}>
      <Input
        type="number"
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) =>
          onChange(e.target.value === "" ? undefined : Number(e.target.value))
        }
        className={clsx(INPUT_CLASS)}
      />
    </FieldRow>
  );
}

export function SelectField({
  label,
  value,
  onChange,
  options,
  hint,
  placeholder = "--select--",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string; disabled?: boolean }>;
  hint?: string;
  placeholder?: string;
}) {
  return (
    <FieldRow label={label} hint={hint}>
      <div className="relative">
        <Select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={clsx(SELECT_CLASS)}
        >
          <option value="">{placeholder}</option>
          {options.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          ))}
        </Select>
        <i
          className="fas fa-chevron-down absolute top-2.5 right-2.5 size-4 pointer-events-none opacity-60"
          aria-hidden="true"
        />
      </div>
    </FieldRow>
  );
}

export function SwitchField({
  label,
  checked,
  onChange,
  hint,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  hint?: string;
}) {
  return (
    <FieldRow label={label} hint={hint}>
      <Switch checked={checked} onChange={onChange} className={SWITCH_CLASS}>
        <span className="size-4 translate-x-1 rounded-full bg-white transition group-data-[checked]:translate-x-6" />
      </Switch>
    </FieldRow>
  );
}
